import { ThemeProvider, createTheme } from '@mui/material';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { Alert, Snackbar } from '@mui/material';
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AppLayout from './AppLayout';
import Footer from './components/Navigation/Footer';
import Navbar from './components/Navigation/Navbar';
import { useAppContext } from './contexts/AppContext';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import About from './pages/About';
import Configurator from './pages/Configurator';
import FAQ from './pages/FAQ';
import Gallery from './pages/Gallery';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import CategoryProducts from './pages/CategoryProducts';
import Checkout from './pages/Checkout';
import Reviews from './pages/Reviews';
import Events from './pages/Events';
import EventDetails from './pages/EventDetail';
import NotFound from './pages/NotFound';
import OrderStatus from './pages/OrderStatus';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ShortLinkRedirect from './pages/ShortLinkRedirect';

// Authentication components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

// Account components
import Account from './pages/account/Account';
import Profile from './pages/account/Profile';
import Addresses from './pages/account/Addresses';
import Orders from './pages/account/Orders';
import OrderDetail from './pages/account/OrderDetail';

// Test component for Supabase integration
import ProductCatalogTest from './components/ProductCatalogTest';

import { ENABLE_CHECKOUT } from './config/featureFlags';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// No longer needed - AWS Amplify is now integrated into the main authentication system

/**
 * React Query Configuration
 * 
 * We use React Query to handle data fetching, caching, and state management.
 * This eliminates duplicate API calls and improves performance.
 * 
 * Configuration details:
 * - staleTime: How long data is considered fresh (5 minutes)
 * - cacheTime: How long unused data is kept in memory (10 minutes)
 * - refetchOnWindowFocus: Disabled to prevent unnecessary refetching when users return to the tab
 * 
 * See docs/react-query-implementation.md for more details
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#8B4513', // Saddle Brown - good for leather theme
      light: '#A0522D',
      dark: '#654321',
    },
    secondary: {
      main: '#D2B48C', // Tan
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
  },
});

function App() {

  const { snackbarMessage, snackbarSeverity, isSnackbarOpen, hideSnackbar } = useAppContext();

  const routes = [
    // Public routes
    { path: "/", element: <Home />, title: "KBros Leather | Handcrafted Leather Goods" },
    { path: "/products", element: <Products />, title: "KBros Leather | Our Products" },
    // Supabase connectivity harness — dev only. It was reachable in production,
    // where it renders raw catalog debug output at a guessable URL.
    ...(process.env.NODE_ENV === 'development'
      ? [{ path: "/products-test", element: <ProductCatalogTest />, title: "KBros Leather | Supabase Test" }]
      : []),
    { path: "/products/category/:category", element: <CategoryProducts />, title: "KBros Leather | Category Products" },
    // Handle both ID and slug-based product URLs with the same component
    { path: "/products/:idOrSlug", element: <ProductDetail />, title: "KBros Leather | Product Details" },
    { path: "/gallery", element: <Gallery />, title: "KBros Leather | Gallery" },
    { path: "/configurator", element: <Configurator />, title: "KBros Leather | Wallet Designer" },
    ...(ENABLE_CHECKOUT ? [{ path: "/checkout", element: <Checkout />, title: "KBros Leather | Checkout" }] : []),
    { path: "/about", element: <About />, title: "KBros Leather | About Us" },
    { path: "/faq", element: <FAQ />, title: "KBros Leather | FAQ" },
    { path: "/reviews", element: <Reviews />, title: "KBros Leather | Reviews" },
    { path: "/events", element: <Events />, title: "KBros Leather | Events" },
    { path: "/events/:id", element: <EventDetails />, title: "KBros Leather | Event Details" },
    { path: "/privacy", element: <Privacy />, title: "KBros Leather | Privacy Policy" },
    { path: "/terms", element: <Terms />, title: "KBros Leather | Terms of Service" },
    // Public order-status lookup — linked from confirmation emails, so it must
    // stay available even when checkout/auth routes are feature-flagged off
    { path: "/orders/:token", element: <OrderStatus />, title: "KBros Leather | Order Status" },
    
    // Authentication + account routes (only active when checkout is enabled)
    ...(ENABLE_CHECKOUT ? [
      { path: "/login", element: <Login />, title: "KBros Leather | Login" },
      { path: "/register", element: <Register />, title: "KBros Leather | Create Account" },
      { path: "/forgot-password", element: <ForgotPassword />, title: "KBros Leather | Forgot Password" },
      { path: "/reset-password", element: <ResetPassword />, title: "KBros Leather | Reset Password" },
      // Protected routes
      { path: "/account", element: <Account />, title: "KBros Leather | My Account", protected: true },
      { path: "/account/profile", element: <Profile />, title: "KBros Leather | My Profile", protected: true },
      { path: "/account/addresses", element: <Addresses />, title: "KBros Leather | My Addresses", protected: true },
      { path: "/account/orders", element: <Orders />, title: "KBros Leather | My Orders", protected: true },
      { path: "/account/orders/:id", element: <OrderDetail />, title: "KBros Leather | Order Details", protected: true },
    ] : []),
  ];

  return (
    <QueryClientProvider client={queryClient}>
      {/* 
        QueryClientProvider makes React Query available throughout the app.
        This provider must wrap the entire application to enable React Query's
        caching and deduplication features across all components.
      */}
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <AppLayout routes={routes}>
                <div className="App">
                  <Navbar />
                  <main style={{ minHeight: 'calc(100vh - 160px)' }}>
                    <Routes>
                      {/* Public routes */}
                      {routes.filter(route => !route.protected).map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                      ))}

                      {/* Protected routes */}
                      <Route element={<ProtectedRoute />}>
                        {routes.filter(route => route.protected).map(route => (
                          <Route key={route.path} path={route.path} element={route.element} />
                        ))}
                      </Route>

                      {/* Short link redirects */}
                      <Route path="/l/:code" element={<ShortLinkRedirect />} />

                      {/* Catch-all: unmatched paths (incl. flag-gated routes when flag=off) */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                  <Snackbar open={isSnackbarOpen} autoHideDuration={3000} onClose={hideSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={hideSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                      {snackbarMessage}
                    </Alert>
                  </Snackbar>
                </div>
              </AppLayout>
            </Router>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
      {/* React Query Devtools - only visible in development */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
